import { 
  BusinessConsultantChat, 
  BusinessConsultantMessage, 
  BusinessConsultantConsent,
  ChatResponse,
  ConsultantType,
  MessageRole,
  ToolCallPayload 
} from '../types/businessConsultant';
import { createAppError, ErrorCategory } from './errorHandler';

/**
 * Data validation utilities for external service responses
 * Ensures type safety and prevents runtime errors from malformed data
 */

/**
 * Validate if a value is a non-empty string
 */
function isValidString(value: any, fieldName: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw createAppError(
      `Invalid ${fieldName}: must be a non-empty string`,
      ErrorCategory.VALIDATION,
      400,
      `Invalid ${fieldName} provided`
    );
  }
  return value.trim();
}

function validateToolCallPayload(data: any): ToolCallPayload {
  if (!data || typeof data !== 'object') {
    throw createAppError(
      'Invalid tool call payload: must be an object',
      ErrorCategory.VALIDATION,
      400,
      'Invalid tool call payload received'
    );
  }

  return {
    tool: isValidString(data.tool, 'tool name'),
    input: data.input && typeof data.input === 'object' ? data.input : undefined,
    result: data.result && typeof data.result === 'object' ? data.result : undefined,
  };
}

export function validateToolCallPayloadArray(data: any): ToolCallPayload[] {
  if (!Array.isArray(data)) {
    throw createAppError(
      'Invalid tool calls data: must be an array',
      ErrorCategory.VALIDATION,
      400,
      'Invalid tool calls received'
    );
  }

  return data.map((item, index) => {
    try {
      return validateToolCallPayload(item);
    } catch (error) {
      if (error instanceof Error) {
        throw createAppError(
          `Tool call validation failed at index ${index}: ${error.message}`,
          ErrorCategory.VALIDATION,
          400,
          'Invalid tool call payload',
          { index, originalData: item }
        );
      }
      throw createAppError(
        `Tool call validation failed at index ${index}: Unknown error`,
        ErrorCategory.VALIDATION,
        400,
        'Invalid tool call payload',
        { index, originalData: item }
      );
    }
  });
}

/**
 * Validate if a value is a valid number
 */
function isValidNumber(value: any, fieldName: string, min: number = 0): number {
  const num = Number(value);
  if (isNaN(num) || num < min) {
    throw createAppError(
      `Invalid ${fieldName}: must be a number >= ${min}`,
      ErrorCategory.VALIDATION,
      400,
      `Invalid ${fieldName} provided`
    );
  }
  return num;
}

/**
 * Validate if a value is a valid boolean
 */
function isValidBoolean(value: any, fieldName: string): boolean {
  if (typeof value !== 'boolean') {
    throw createAppError(
      `Invalid ${fieldName}: must be a boolean`,
      ErrorCategory.VALIDATION,
      400,
      `Invalid ${fieldName} provided`
    );
  }
  return value;
}

/**
 * Validate if a value is a valid ISO date string
 */
function isValidDateString(value: any, fieldName: string): string {
  const dateStr = isValidString(value, fieldName);
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    throw createAppError(
      `Invalid ${fieldName}: must be a valid ISO date string`,
      ErrorCategory.VALIDATION,
      400,
      `Invalid ${fieldName} format`
    );
  }
  return dateStr;
}

/**
 * Validate consultant type
 */
function isValidConsultantType(value: any): ConsultantType {
  const validTypes: ConsultantType[] = ['marketing', 'finance', 'strategy'];
  if (!validTypes.includes(value)) {
    throw createAppError(
      `Invalid consultant type: must be one of ${validTypes.join(', ')}`,
      ErrorCategory.VALIDATION,
      400,
      'Invalid consultant type'
    );
  }
  return value as ConsultantType;
}

/**
 * Validate message role
 */
function isValidMessageRole(value: any): MessageRole {
  const validRoles: MessageRole[] = ['user', 'assistant', 'system'];
  if (!validRoles.includes(value)) {
    throw createAppError(
      `Invalid message role: must be one of ${validRoles.join(', ')}`,
      ErrorCategory.VALIDATION,
      400,
      'Invalid message role'
    );
  }
  return value as MessageRole;
}

/**
 * Validate BusinessConsultantChat from external data
 */
export function validateBusinessConsultantChat(data: any): BusinessConsultantChat {
  if (!data || typeof data !== 'object') {
    throw createAppError(
      'Invalid chat data: must be an object',
      ErrorCategory.VALIDATION,
      400,
      'Invalid chat data received'
    );
  }

  try {
    return {
      id: isValidString(data.id, 'chat ID'),
      user_id: isValidString(data.user_id, 'user ID'),
      chat_name: isValidString(data.chat_name, 'chat name'),
      consultant_type: isValidConsultantType(data.consultant_type),
      idea_id: data.idea_id ? isValidString(data.idea_id, 'idea ID') : undefined,
      business_plan_id: data.business_plan_id ? isValidString(data.business_plan_id, 'business plan ID') : undefined,
      total_tokens_used: isValidNumber(data.total_tokens_used, 'total tokens used'),
      is_archived: isValidBoolean(data.is_archived, 'is archived'),
      created_at: isValidDateString(data.created_at, 'created at'),
      updated_at: isValidDateString(data.updated_at, 'updated at')
    };
  } catch (error) {
    if (error instanceof Error) {
      throw createAppError(
        `Chat validation failed: ${error.message}`,
        ErrorCategory.VALIDATION,
        400,
        'Invalid chat data format',
        { originalData: data }
      );
    } else {
      throw createAppError(
        'Chat validation failed: Unknown error',
        ErrorCategory.VALIDATION,
        400,
        'Invalid chat data format',
        { originalData: data }
      );
    }
  }
}

/**
 * Validate BusinessConsultantMessage from external data
 */
export function validateBusinessConsultantMessage(data: any): BusinessConsultantMessage {
  if (!data || typeof data !== 'object') {
    throw createAppError(
      'Invalid message data: must be an object',
      ErrorCategory.VALIDATION,
      400,
      'Invalid message data received'
    );
  }

  try {
    return {
      id: isValidString(data.id, 'message ID'),
      chat_id: isValidString(data.chat_id, 'chat ID'),
      role: isValidMessageRole(data.role),
      content: isValidString(data.content, 'message content'),
      tokens_used: isValidNumber(data.tokens_used, 'tokens used'),
      created_at: isValidDateString(data.created_at, 'created at')
    };
  } catch (error) {
    if (error instanceof Error) {
      throw createAppError(
        `Message validation failed: ${error.message}`,
        ErrorCategory.VALIDATION,
        400,
        'Invalid message data format',
        { originalData: data }
      );
    } else {
      throw createAppError(
        'Message validation failed: Unknown error',
        ErrorCategory.VALIDATION,
        400,
        'Invalid message data format',
        { originalData: data }
      );
    }
  }
}

/**
 * Validate BusinessConsultantConsent from external data
 */
export function validateBusinessConsultantConsent(data: any): BusinessConsultantConsent {
  if (!data || typeof data !== 'object') {
    throw createAppError(
      'Invalid consent data: must be an object',
      ErrorCategory.VALIDATION,
      400,
      'Invalid consent data received'
    );
  }

  try {
    return {
      id: isValidString(data.id, 'consent ID'),
      user_id: isValidString(data.user_id, 'user ID'),
      has_consented: isValidBoolean(data.has_consented, 'has consented'),
      created_at: isValidDateString(data.created_at, 'created at'),
      updated_at: isValidDateString(data.updated_at, 'updated at')
    };
  } catch (error) {
    if (error instanceof Error) {
      throw createAppError(
        `Consent validation failed: ${error.message}`,
        ErrorCategory.VALIDATION,
        400,
        'Invalid consent data format',
        { originalData: data }
      );
    } else {
      throw createAppError(
        'Consent validation failed: Unknown error',
        ErrorCategory.VALIDATION,
        400,
        'Invalid consent data format',
        { originalData: data }
      );
    }
  }
}

/**
 * Validate ChatResponse from external API
 */
export function validateChatResponse(data: any): ChatResponse {
  if (!data || typeof data !== 'object') {
    throw createAppError(
      'Invalid chat response: must be an object',
      ErrorCategory.VALIDATION,
      400,
      'Invalid response from chat service'
    );
  }

  try {
    const toolCalls = data.tool_calls === undefined ? undefined : validateToolCallPayloadArray(data.tool_calls);
    return {
      message: isValidString(data.message, 'response message'),
      tokens_used: isValidNumber(data.tokens_used, 'tokens used'),
      tool_calls: toolCalls
    };
  } catch (error) {
    if (error instanceof Error) {
      throw createAppError(
        `Chat response validation failed: ${error.message}`,
        ErrorCategory.VALIDATION,
        400,
        'Invalid chat service response',
        { originalData: data }
      );
    } else {
      throw createAppError(
        'Chat response validation failed: Unknown error',
        ErrorCategory.VALIDATION,
        400,
        'Invalid chat service response',
        { originalData: data }
      );
    }
  }
}

/**
 * Validate array of BusinessConsultantChat objects
 */
export function validateBusinessConsultantChatArray(data: any): BusinessConsultantChat[] {
  if (!Array.isArray(data)) {
    throw createAppError(
      'Invalid chats data: must be an array',
      ErrorCategory.VALIDATION,
      400,
      'Invalid chats data received'
    );
  }

  try {
    return data.map((item, index) => {
      try {
        return validateBusinessConsultantChat(item);
      } catch (error) {
        if (error instanceof Error) {
          throw createAppError(
            `Chat validation failed at index ${index}: ${error.message}`,
            ErrorCategory.VALIDATION,
            400,
            'Invalid chat data in response',
            { index, originalData: item }
          );
        } else {
          throw createAppError(
            `Chat validation failed at index ${index}: Unknown error`,
            ErrorCategory.VALIDATION,
            400,
            'Invalid chat data in response',
            { index, originalData: item }
          );
        }
      }
    });
  } catch (error) {
    throw error;
  }
}

/**
 * Validate array of BusinessConsultantMessage objects
 */
export function validateBusinessConsultantMessageArray(data: any): BusinessConsultantMessage[] {
  if (!Array.isArray(data)) {
    throw createAppError(
      'Invalid messages data: must be an array',
      ErrorCategory.VALIDATION,
      400,
      'Invalid messages data received'
    );
  }

  try {
    return data.map((item, index) => {
      try {
        return validateBusinessConsultantMessage(item);
      } catch (error) {
        if (error instanceof Error) {
          throw createAppError(
            `Message validation failed at index ${index}: ${error.message}`,
            ErrorCategory.VALIDATION,
            400,
            'Invalid message data in response',
            { index, originalData: item }
          );
        } else {
          throw createAppError(
            `Message validation failed at index ${index}: Unknown error`,
            ErrorCategory.VALIDATION,
            400,
            'Invalid message data in response',
            { index, originalData: item }
          );
        }
      }
    });
  } catch (error) {
    throw error;
  }
}

/**
 * Safely parse JSON with validation
 */
export function safeJsonParse<T>(
  jsonString: string, 
  validator?: (data: any) => T,
  context?: string
): T {
  try {
    const parsed = JSON.parse(jsonString);
    
    if (validator) {
      return validator(parsed);
    }
    
    return parsed;
  } catch (error) {
    if (error instanceof Error) {
      const contextMsg = context ? ` (${context})` : '';
      throw createAppError(
        `JSON parse error${contextMsg}: ${error.message}`,
        ErrorCategory.VALIDATION,
        400,
        'Invalid data format received',
        { originalString: jsonString }
      );
    } else {
      const contextMsg = context ? ` (${context})` : '';
      throw createAppError(
        `JSON parse error${contextMsg}: Unknown error`,
        ErrorCategory.VALIDATION,
        400,
        'Invalid data format received',
        { originalString: jsonString }
      );
    }
  }
}
